import type {
  PaginaFaturamento,
  LinhaFaturamento,
  FiltroFaturamento,
  StatusOperacionalPedido,
} from '@pgcustom/shared-types';
import {
  ROTULO_STATUS_OPERACIONAL,
  STATUS_OPERACIONAL_PEDIDO,
  ROTULO_STATUS_FISCAL,
  rotuloOrigemPedido,
  ROTULO_ORIGEM_PEDIDO,
} from '@pgcustom/shared-types';
import { formatBRL, centavosParaReais } from '@pgcustom/shared-utils';
import type { Resultado } from '../../core/resultado.js';
import { MARCA_PG } from '../../core/app-shell/marca.js';
import type { FaturamentoApi } from './faturamento.api.js';
import type { PermissoesFaturamento } from './faturamento.permissoes.js';

const PERIODOS: Array<{ valor: string; rotulo: string; dias: number | null }> = [
  { valor: '', rotulo: 'PERÍODO: TODOS', dias: null },
  { valor: 'mes', rotulo: 'ESTE MÊS', dias: 30 },
  { valor: 'trimestre', rotulo: 'ÚLTIMOS 90 DIAS', dias: 90 },
  { valor: 'ano', rotulo: 'ÚLTIMOS 12 MESES', dias: 365 },
];

function dataCurta(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso));
}

function brl(centavos: number | null): string {
  return centavos === null ? '—' : formatBRL(centavosParaReais(centavos));
}

function esc(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

/**
 * `<erp-faturamento-page>` — "Pedidos / Faturamento" (referência visual
 * `pg_custom_planilha_faturamento_com_logo.html`). Só leitura: lista
 * `comercial.pedidos` com `status = 'pago'`, ordenados por confirmação
 * de pagamento (mais recente primeiro). KPIs e valores vêm do backend
 * em centavos — nenhum número é hardcoded, nenhum dado é inventado.
 */
export class ErpFaturamentoPage extends HTMLElement {
  private readonly root: ShadowRoot;
  private _api: FaturamentoApi | null = null;
  private _permissoes: PermissoesFaturamento = { ler: false };

  private resultado: Resultado<PaginaFaturamento> = { status: 'carregando' };
  private filtro: FiltroFaturamento = {};
  private filtroModelo = '';
  private filtroPeriodo = '';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set faturamentoApi(valor: FaturamentoApi) {
    this._api = valor;
    if (this.isConnected) void this.carregar();
  }

  set permissoes(valor: PermissoesFaturamento) {
    this._permissoes = valor;
    if (this.isConnected) this.render();
  }

  connectedCallback(): void {
    this.render();
    if (this._api) void this.carregar();
  }

  private async carregar(): Promise<void> {
    if (!this._api) return;
    if (!this._permissoes.ler) {
      this.resultado = { status: 'sem-permissao' };
      this.render();
      return;
    }
    this.resultado = { status: 'carregando' };
    this.render();
    this.resultado = await this._api.listar({ ...this.filtro, limit: 500 });
    this.render();
  }

  private linhasVisiveis(): LinhaFaturamento[] {
    if (this.resultado.status !== 'ok') return [];
    const todas = this.resultado.dado.linhas;
    if (!this.filtroModelo) return todas;
    return todas.filter((l) => (l.modelo ?? '').toLowerCase().includes(this.filtroModelo.toLowerCase()));
  }

  private modelosDisponiveis(): string[] {
    if (this.resultado.status !== 'ok') return [];
    const set = new Set<string>();
    for (const l of this.resultado.dado.linhas) if (l.modelo) set.add(l.modelo);
    return [...set].sort((a, b) => a.localeCompare(b));
  }

  private aplicarPeriodo(valor: string): void {
    this.filtroPeriodo = valor;
    const p = PERIODOS.find((x) => x.valor === valor);
    if (!p || p.dias === null) {
      delete this.filtro.pagoDesde;
    } else {
      const d = new Date();
      d.setDate(d.getDate() - p.dias);
      this.filtro.pagoDesde = d.toISOString();
    }
    void this.carregar();
  }

  private limparFiltros(): void {
    this.filtro = {};
    this.filtroModelo = '';
    this.filtroPeriodo = '';
    void this.carregar();
  }

  private exportarCsv(): void {
    const linhas = this.linhasVisiveis();
    const cabecalho = [
      'DATA',
      'NOME',
      'E-MAIL',
      'NF',
      'STATUS FISCAL',
      'VALOR TOTAL',
      'SUBTOTAL',
      'DESCONTO',
      'FRETE',
      'MODELO DO CONTROLE',
      'STATUS DO PEDIDO',
      'DESCRIÇÃO DAS MODIFICAÇÕES E VALORES',
      'ORIGEM',
      'CUPOM',
      'PARCEIRO',
    ];
    const corpo = linhas.map((l) => [
      dataCurta(l.pagoEm),
      l.clienteNome ?? '',
      l.clienteEmail ?? '',
      l.numero ?? 'AGUARDANDO EMISSÃO',
      ROTULO_STATUS_FISCAL[l.statusFiscal],
      brl(l.valorTotalCentavos),
      brl(l.subtotalCentavos),
      brl(l.descontoCentavos),
      brl(l.freteCentavos),
      l.modelo ?? '',
      l.statusOperacional ? ROTULO_STATUS_OPERACIONAL[l.statusOperacional] : '',
      l.descricaoModificacoes,
      rotuloOrigemPedido(l.origem),
      l.cupomCodigo ?? '',
      l.parceiroNome ?? '',
    ]);
    const csv = [cabecalho, ...corpo]
      .map((linha) => linha.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';'))
      .join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `faturamento-pg-custom-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  private render(): void {
    const podeLer = this._permissoes.ler;
    const resumo =
      this.resultado.status === 'ok'
        ? this.resultado.dado.resumo
        : { pedidosPagos: 0, faturamentoCentavos: 0, nfEmitidas: 0, nfPendentes: 0 };

    this.root.innerHTML = `
      <style>${CSS}</style>
      <header class="head">
        <div class="head-id">
          <img class="head-emblema" src="${MARCA_PG.emblemaOuro}" alt="" aria-hidden="true" />
          <div>
            <h1>Pedidos / Faturamento</h1>
            <div class="muted">Todos os pedidos pagos • faturamento • identificação fiscal</div>
          </div>
        </div>
        <div class="acoes">
          <button class="btn" data-acao="exportar" ${podeLer ? '' : 'disabled'}>Exportar CSV</button>
          <button class="btn primario" data-acao="atualizar" ${podeLer ? '' : 'disabled'}>Atualizar</button>
        </div>
      </header>

      ${
        podeLer
          ? `
      <div class="kpis">
        <div class="kpi"><div class="lab">Pedidos pagos</div><div class="num">${resumo.pedidosPagos}</div><div class="muted">A partir do filtro aplicado</div></div>
        <div class="kpi"><div class="lab">Faturamento</div><div class="num ouro">${brl(resumo.faturamentoCentavos)}</div><div class="muted">Pedidos com pagamento confirmado</div></div>
        <div class="kpi"><div class="lab">NF emitidas</div><div class="num verde">${resumo.nfEmitidas}</div><div class="muted">Com número comercial (NFPG)</div></div>
        <div class="kpi"><div class="lab">NF pendentes</div><div class="num rosa">${resumo.nfPendentes}</div><div class="muted">Aguardando emissão</div></div>
      </div>

      <section class="sec">
        <div class="sectop">
          <div>
            <h2>Faturamento dos pedidos pagos</h2>
            <div class="muted">Ordenação padrão: pagamento mais recente</div>
          </div>
          <span class="badge ok">Atualização automática</span>
        </div>

        <div class="toolbar">
          <input id="busca" class="campo" type="search" placeholder="Buscar cliente, NF, e-mail ou modelo" value="${esc(this.filtro.busca ?? '')}" />
          <select id="periodo" class="campo">
            ${PERIODOS.map((p) => `<option value="${p.valor}" ${p.valor === this.filtroPeriodo ? 'selected' : ''}>${esc(p.rotulo)}</option>`).join('')}
          </select>
          <select id="status" class="campo">
            <option value="">STATUS: TODOS</option>
            ${STATUS_OPERACIONAL_PEDIDO.map((s) => `<option value="${s}" ${s === this.filtro.statusOperacional ? 'selected' : ''}>${esc(ROTULO_STATUS_OPERACIONAL[s].toUpperCase())}</option>`).join('')}
          </select>
          <select id="modelo" class="campo">
            <option value="">MODELO: TODOS</option>
            ${this.modelosDisponiveis().map((m) => `<option value="${esc(m)}" ${m === this.filtroModelo ? 'selected' : ''}>${esc(m.toUpperCase())}</option>`).join('')}
          </select>
          <select id="fiscal" class="campo">
            <option value="">NF: TODAS</option>
            <option value="emitida" ${this.filtro.statusFiscal === 'emitida' ? 'selected' : ''}>EMITIDAS (NFPG)</option>
            <option value="aguardando_emissao" ${this.filtro.statusFiscal === 'aguardando_emissao' ? 'selected' : ''}>AGUARDANDO EMISSÃO</option>
          </select>
          <select id="origem" class="campo">
            <option value="">ORIGEM: TODAS</option>
            ${Object.entries(ROTULO_ORIGEM_PEDIDO).map(([cod, rot]) => `<option value="${cod}" ${cod === this.filtro.origem ? 'selected' : ''}>${esc(rot.toUpperCase())}</option>`).join('')}
          </select>
          <button class="btn" data-acao="limpar">Limpar filtros</button>
        </div>

        <div class="tabela-wrap">${this.tabelaHtml()}</div>
      </section>

      <div class="nota">
        <b>Regra de integridade:</b> só entra pedido com pagamento efetivamente confirmado
        (a data de referência é a confirmação do pagamento, nunca a criação do pedido).
        Pedidos pendentes ou cancelados não entram. O status operacional é independente do
        status fiscal e financeiro. Não há emissão NF-e — o número comercial <b>NFPG####</b>
        (atribuído no pagamento, único e imutável) é a nota que a PG Custom usa hoje.
      </div>
      `
          : `<div class="nota sem-acesso">Você não tem permissão para ver o faturamento (recurso <b>financeiro</b>).</div>`
      }
    `;

    this.ligarEventos();
  }

  private tabelaHtml(): string {
    if (this.resultado.status === 'carregando') return `<div class="estado">Carregando faturamento…</div>`;
    if (this.resultado.status === 'sem-permissao') return `<div class="estado">Sem permissão.</div>`;
    if (this.resultado.status === 'erro') return `<div class="estado erro">${esc(this.resultado.mensagem)}</div>`;

    const linhas = this.linhasVisiveis();
    if (linhas.length === 0) {
      return `<div class="estado">Nenhum pedido pago no filtro atual.</div>`;
    }

    return `
      <table>
        <thead>
          <tr>
            <th>DATA</th><th>NOME</th><th>NF</th><th>VALOR TOTAL</th>
            <th>MODELO DO CONTROLE</th><th>STATUS DO PEDIDO</th>
            <th>DESCRIÇÃO DAS MODIFICAÇÕES E VALORES</th><th>ORIGEM</th><th>AÇÃO</th>
          </tr>
        </thead>
        <tbody>
          ${linhas.map((l) => this.linhaHtml(l)).join('')}
        </tbody>
      </table>
    `;
  }

  private linhaHtml(l: LinhaFaturamento): string {
    const nf = l.numero
      ? `<span class="nf">${esc(l.numero)}</span>`
      : `<span class="badge pendente">${esc(ROTULO_STATUS_FISCAL.aguardando_emissao.toUpperCase())}</span>`;
    const statusOp = l.statusOperacional
      ? `<span class="badge ${tomStatus(l.statusOperacional)}">${esc(ROTULO_STATUS_OPERACIONAL[l.statusOperacional].toUpperCase())}</span>`
      : `<span class="muted">—</span>`;
    return `
      <tr>
        <td>${esc(dataCurta(l.pagoEm))}</td>
        <td><b>${esc(l.clienteNome ?? '—')}</b>${l.clienteEmail ? `<div class="muted">${esc(l.clienteEmail)}</div>` : ''}</td>
        <td>${nf}</td>
        <td><b>${esc(brl(l.valorTotalCentavos))}</b></td>
        <td>${esc(l.modelo ?? '—')}</td>
        <td>${statusOp}</td>
        <td class="desc">${esc(l.descricaoModificacoes)}</td>
        <td>${esc(rotuloOrigemPedido(l.origem).toUpperCase())}</td>
        <td><button class="link" data-pedido="${esc(l.pedidoId)}">Ver pedido</button></td>
      </tr>
    `;
  }

  private ligarEventos(): void {
    this.root.querySelector('[data-acao="atualizar"]')?.addEventListener('click', () => void this.carregar());
    this.root.querySelector('[data-acao="exportar"]')?.addEventListener('click', () => this.exportarCsv());
    this.root.querySelector('[data-acao="limpar"]')?.addEventListener('click', () => this.limparFiltros());

    const busca = this.root.querySelector<HTMLInputElement>('#busca');
    busca?.addEventListener('change', () => {
      const v = busca.value.trim();
      if (v) this.filtro.busca = v;
      else delete this.filtro.busca;
      void this.carregar();
    });

    this.root.querySelector<HTMLSelectElement>('#periodo')?.addEventListener('change', (e) => {
      this.aplicarPeriodo((e.target as HTMLSelectElement).value);
    });
    this.root.querySelector<HTMLSelectElement>('#status')?.addEventListener('change', (e) => {
      const v = (e.target as HTMLSelectElement).value as StatusOperacionalPedido | '';
      if (v) this.filtro.statusOperacional = v;
      else delete this.filtro.statusOperacional;
      void this.carregar();
    });
    this.root.querySelector<HTMLSelectElement>('#fiscal')?.addEventListener('change', (e) => {
      const v = (e.target as HTMLSelectElement).value;
      if (v === 'emitida' || v === 'aguardando_emissao') this.filtro.statusFiscal = v;
      else delete this.filtro.statusFiscal;
      void this.carregar();
    });
    this.root.querySelector<HTMLSelectElement>('#origem')?.addEventListener('change', (e) => {
      const v = (e.target as HTMLSelectElement).value;
      if (v) this.filtro.origem = v;
      else delete this.filtro.origem;
      void this.carregar();
    });
    this.root.querySelector<HTMLSelectElement>('#modelo')?.addEventListener('change', (e) => {
      this.filtroModelo = (e.target as HTMLSelectElement).value;
      this.render();
    });

    this.root.querySelectorAll<HTMLButtonElement>('[data-pedido]').forEach((botao) => {
      botao.addEventListener('click', () => {
        this.dispatchEvent(
          new CustomEvent('erp-navegar', { bubbles: true, composed: true, detail: { caminho: '/pedidos' } })
        );
      });
    });
  }
}

function tomStatus(status: StatusOperacionalPedido): string {
  if (status === 'entregue' || status === 'enviado') return 'ok';
  if (status === 'solicitado') return 'pendente';
  return 'andamento';
}

const CSS = `
  :host { display: block; font-family: var(--pg-font-familia-base, system-ui); color: var(--pg-color-texto-primario, #f2f3f5); }
  .muted { color: var(--pg-color-texto-secundario, #9aa1ab); font-size: 11px; }
  .head { display: flex; justify-content: space-between; align-items: flex-end; gap: 16px; margin-bottom: 20px; flex-wrap: wrap; }
  .head-id { display: flex; align-items: center; gap: 12px; }
  .head-emblema { width: 34px; height: 34px; object-fit: contain; filter: drop-shadow(0 0 6px rgba(201,162,75,0.18)); }
  h1 { font-family: var(--pg-font-familia-display, system-ui); font-size: 1.5rem; margin: 0 0 2px; }
  h2 { font-family: var(--pg-font-familia-display, system-ui); font-size: 1.05rem; margin: 0 0 2px; }
  .acoes { display: flex; gap: 8px; }
  .btn { border: 1px solid var(--pg-color-borda, #2a2f36); background: var(--pg-color-bg-elevado, #1a1d21); color: inherit; border-radius: 8px; padding: 9px 14px; font-size: 12px; font-weight: 600; cursor: pointer; }
  .btn:hover:not(:disabled) { background: var(--pg-color-bg-hover, #22262b); }
  .btn:disabled { opacity: 0.45; cursor: not-allowed; }
  .btn.primario { background: linear-gradient(135deg, var(--pg-color-dourado, #c9a24b), #9c7c33); border-color: transparent; color: #14100a; }
  .kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 24px; }
  @media (max-width: 900px) { .kpis { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 560px) { .kpis { grid-template-columns: 1fr; } }
  .kpi { background: var(--pg-color-bg-elevado, #171b20); border: 1px solid var(--pg-color-borda, #2a2f36); border-radius: 10px; padding: 16px; }
  .kpi .lab { font-size: 10px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; color: var(--pg-color-texto-secundario, #89929e); }
  .kpi .num { font-size: 1.5rem; font-weight: 800; margin: 6px 0 2px; }
  .ouro { color: var(--pg-color-dourado, #c9a24b); }
  .verde { color: var(--pg-color-sucesso, #3bb273); }
  .rosa { color: var(--pg-color-rosa-pg-art, #e05f9b); }
  .sec { margin-top: 8px; }
  .sectop { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 12px; gap: 12px; flex-wrap: wrap; }
  .toolbar { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
  .campo { background: var(--pg-color-bg-base, #111418); color: inherit; border: 1px solid var(--pg-color-borda, #2a2f36); border-radius: 7px; padding: 9px 11px; font-size: 12px; }
  .campo[type="search"] { min-width: 240px; flex: 1 1 240px; }
  .badge { display: inline-block; padding: 4px 8px; border-radius: 20px; font-size: 10px; font-weight: 800; letter-spacing: 0.02em; }
  .badge.ok { color: var(--pg-color-sucesso, #3bb273); background: rgba(66,201,139,0.14); }
  .badge.pendente { color: #5ca4ff; background: rgba(92,164,255,0.14); }
  .badge.andamento { color: var(--pg-color-dourado, #c9a24b); background: rgba(201,162,75,0.14); }
  .tabela-wrap { overflow-x: auto; border: 1px solid var(--pg-color-borda, #2a2f36); border-radius: 10px; background: var(--pg-color-bg-elevado, #15191d); }
  table { border-collapse: collapse; width: 100%; min-width: 1400px; }
  th, td { padding: 12px 13px; border-bottom: 1px solid var(--pg-color-borda, #252a31); text-align: left; white-space: nowrap; vertical-align: top; }
  th { font-size: 10px; letter-spacing: 0.06em; color: var(--pg-color-texto-secundario, #7f8996); font-weight: 800; }
  td { font-size: 12px; }
  tr:last-child td { border-bottom: 0; }
  td.desc { white-space: normal; min-width: 400px; max-width: 620px; line-height: 1.5; }
  .nf { font-weight: 800; letter-spacing: 0.03em; color: var(--pg-color-dourado, #c9a24b); }
  .link { background: none; border: 0; color: var(--pg-color-texto-secundario, #9da6b2); font-size: 12px; cursor: pointer; text-decoration: underline; padding: 0; }
  .link:hover { color: var(--pg-color-texto-primario, #f2f3f5); }
  .estado { padding: 40px 16px; text-align: center; color: var(--pg-color-texto-secundario, #9aa1ab); font-size: 13px; }
  .estado.erro { color: var(--pg-color-erro, #e5484d); }
  .nota { margin-top: 18px; border: 1px solid rgba(201,162,75,0.28); background: rgba(201,162,75,0.06); color: var(--pg-color-texto-secundario, #b9b09a); border-radius: 8px; padding: 12px 14px; font-size: 11px; line-height: 1.7; }
  .nota.sem-acesso { border-color: var(--pg-color-borda, #2a2f36); background: var(--pg-color-bg-elevado, #1a1d21); }
`;

customElements.define('erp-faturamento-page', ErpFaturamentoPage);

declare global {
  interface HTMLElementTagNameMap {
    'erp-faturamento-page': ErpFaturamentoPage;
  }
}
